import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, CalendarDays, MapPin, ImageIcon, Shield } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUB = `${BACKEND}/api/public`;

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${PUB}/${token}/summary`);
        if (!res.ok) throw new Error("bad");
        const d = await res.json();
        setData(d);
      } catch (e) {
        setError("Lien invalide ou expiré");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F17]">
        <div className="text-slate-400 font-mono-stat uppercase text-sm">Chargement...</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F17] p-6">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">🔒</div>
          <div className="font-display font-black text-2xl uppercase mb-2">{error || "Erreur"}</div>
          <div className="text-slate-400 text-sm">Contactez le coach pour un nouveau lien.</div>
        </div>
      </div>
    );
  }

  const { club_name, logo_url, duel, leaderboard, recent_matches } = data;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white">
      <header className="border-b border-white/10 bg-[#0B0F17]/95 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          {logo_url ? (
            <img
              src={logo_url}
              alt="Logo"
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#E5B85C] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#0B0F17]" />
            </div>
          )}
          <div>
            <div className="font-display font-black text-lg uppercase">{club_name}</div>
            <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-500">
              Vitrine publique
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Duel */}
        <section>
          <h2 className="font-display font-black text-3xl uppercase tracking-tight mb-4">
            Duel Bleus vs Rouges
          </h2>
          <div className="rounded-2xl bg-slate-900 border border-white/10 p-6">
            <div className="grid grid-cols-3 gap-4 text-center items-center">
              <div>
                <div className="text-xs font-mono-stat uppercase text-sky-400 mb-2">Bleus</div>
                <div className="font-display font-black text-5xl text-sky-400">
                  {duel.bleus_wins}
                </div>
              </div>
              <div>
                <div className="text-xs font-mono-stat uppercase text-slate-400 mb-2">Nuls</div>
                <div className="font-display font-black text-3xl text-slate-400">{duel.draws}</div>
              </div>
              <div>
                <div className="text-xs font-mono-stat uppercase text-rose-400 mb-2">Rouges</div>
                <div className="font-display font-black text-5xl text-rose-400">
                  {duel.rouges_wins}
                </div>
              </div>
            </div>
            <div className="mt-4 text-center text-xs font-mono-stat uppercase text-slate-500">
              {duel.matches_played} match(s) joué(s)
            </div>
          </div>
        </section>

        {/* Leaderboard */}
        <section>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-[#E5B85C]" /> Meilleurs buteurs
          </h2>
          <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
            {leaderboard.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">Pas encore de buteur</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {leaderboard.map((p, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-3 font-mono-stat text-slate-500 w-10">{i + 1}</td>
                      <td className="p-3 font-bold">{p.player_name}</td>
                      <td className="p-3 text-right font-mono-stat text-slate-400 w-24">
                        {p.matches_played} matchs
                      </td>
                      <td className="p-3 text-right font-mono-stat font-bold text-[#E5B85C] w-16">
                        {p.goals}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Recent matches */}
        <section>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">
            Derniers matchs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recent_matches.length === 0 && (
              <div className="col-span-2 p-6 rounded-xl border border-dashed border-white/10 text-center text-slate-500">
                Aucun match terminé
              </div>
            )}
            {recent_matches.map((m) => (
              <PublicMatchCard key={m.id} token={token} match={m} />
            ))}
          </div>
        </section>

        <footer className="pt-8 pb-4 text-center text-xs font-mono-stat uppercase tracking-widest text-slate-500">
          © {club_name}
        </footer>
      </main>
    </div>
  );
}

function PublicMatchCard({ token, match }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!match.has_photo) return;
    let alive = true;
    let localUrl = null;
    (async () => {
      try {
        const res = await fetch(`${PUB}/${token}/photo/${match.id}`);
        if (!res.ok) return;
        const blob = await res.blob();
        if (!alive) return;
        localUrl = URL.createObjectURL(blob);
        setSrc(localUrl);
      } catch (e) {}
    })();
    return () => {
      alive = false;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [token, match.id, match.has_photo]);

  return (
    <div className="rounded-xl bg-slate-900 border border-white/10 overflow-hidden">
      <div className="aspect-video bg-slate-800 overflow-hidden">
        {src ? (
          <img src={src} alt={match.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-slate-700" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="font-display font-bold uppercase text-sm truncate">{match.title}</div>
        <div className="flex items-center gap-3 text-xs text-slate-400 font-mono-stat mt-1">
          <span className="flex items-center gap-1">
            <CalendarDays className="w-3 h-3" /> {formatDate(match.match_date)}
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {match.location}
          </span>
        </div>
        <div className="mt-2 font-mono-stat font-bold flex items-center gap-2">
          <span className="text-sky-400">Bleus {match.score_bleus}</span>
          <span className="text-slate-500">-</span>
          <span className="text-rose-400">{match.score_rouges} Rouges</span>
        </div>
      </div>
    </div>
  );
}
